<script setup lang="ts">
import { Sketch } from '@ckpack/vue-color'

const { t } = useI18n()

const model = defineModel<string>({ required: true })

withDefaults(
  defineProps<{
    presets?: { color: string; label: string }[]
    showClear?: boolean
  }>(),
  {
    presets: () => [],
    showClear: false
  }
)

const emit = defineEmits<{
  (e: 'clear'): void
}>()

const selectPreset = (color: string) => {
  model.value = color
}

const isEyeDropperSupported = 'EyeDropper' in window

const pickColorFromScreen = async () => {
  const eyeDropper = new (window as any).EyeDropper()
  try {
    const result = await eyeDropper.open()
    model.value = result.sRGBHex.replace('#', '').toLowerCase()
  } catch {
    // User cancelled the eyedropper
  }
}

// Bridge: model is a hex string without '#' (e.g. "ff0000").
// Sketch accepts a hex string and emits an object { hex, hex8, ... }.
const colorModel = computed({
  get: () => `#${model.value}`,
  set: (val: any) => {
    const hex: string = typeof val === 'string' ? val : (val?.hex ?? val?.hex8 ?? '')
    model.value = hex.replace('#', '').toLowerCase()
  }
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="presets.length > 0" class="flex flex-col gap-2">
      <span class="text-xs font-medium text-muted-foreground">
        {{ t('pages.email.emailTemplateCreatePage.panel.suggestedColors') }}
      </span>
      <div class="flex gap-2">
        <button
          v-for="preset in presets"
          :key="preset.color"
          type="button"
          class="group relative h-5 w-5 cursor-pointer rounded-lg ring-1 ring-inset ring-black/10 transition-all hover:scale-105 hover:shadow-md"
          :class="model === preset.color ? 'ring-2 ring-foreground ring-offset-1' : ''"
          :style="{ backgroundColor: `#${preset.color}` }"
          @click="selectPreset(preset.color)"
        >
          <span
            class="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            {{ preset.label }}
          </span>
        </button>

        <button
          v-if="showClear"
          type="button"
          class="group relative flex h-5 w-5 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border bg-white transition-all hover:scale-105 hover:border-border"
          @click="emit('clear')"
        >
          <BaseIcon name="IconoirXMark" class="h-3 w-3 text-muted-foreground" />
          <span
            class="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            {{ t('pages.email.emailTemplateCreatePage.toolbar.none') }}
          </span>
        </button>

        <button
          v-if="isEyeDropperSupported"
          type="button"
          class="group relative flex h-5 w-5 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border bg-white transition-all hover:scale-105 hover:border-border"
          :title="t('pages.email.emailTemplateCreatePage.toolbar.eyeDropper')"
          @click="pickColorFromScreen"
        >
          <BaseIcon name="HeroEyeDropperIconOutline" class="h-3 w-3 text-muted-foreground" />
          <span
            class="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
          >
            {{ t('pages.email.emailTemplateCreatePage.toolbar.eyeDropper') }}
          </span>
        </button>
      </div>
    </div>

    <Sketch
      v-model="colorModel"
      :disable-alpha="true"
      :disable-fields="true"
      :preset-colors="[]"
      class="sketch-no-presets"
    />
  </div>
</template>

<style scoped>
.sketch-no-presets {
  box-shadow: none;
  padding: 0;
}

.sketch-no-presets :deep(.vc-sketch-presets) {
  display: none;
}
</style>
